/**
 * Traduction des erreurs de formulaire.
 *
 * Les formulaires sont validés avec **les schémas zod de `@elemora/shared`** : ce sont exactement
 * les règles appliquées par le serveur (DECISIONS §4.1, « une seule vérité, deux usages »). Les
 * messages produits par zod sont en français et ne conviennent donc pas à une interface bilingue :
 * on ne conserve que le **champ** et le **type d’erreur**, et on choisit une clé du catalogue
 * (`ui:validation.<champ>.<motif>`).
 */
import { instanceI18n } from '../i18n';

import type { TFunction } from 'i18next';
import type { z } from 'zod';


export interface ErreurChamp {
  /** Clé du catalogue d’interface (namespace `ui` par défaut). */
  readonly cle: string;
  /** Valeurs d’interpolation (`{{min}}`, `{{max}}`). */
  readonly valeurs?: Record<string, string | number>;
}

/** Erreurs indexées par nom de champ du schéma zod. */
export type ErreursChamps = Readonly<Record<string, ErreurChamp>>;

/** Vrai si la saisie est vide (chaîne blanche comprise). */
function estVide(valeur: unknown): boolean {
  if (valeur === undefined || valeur === null) return true;
  return typeof valeur === 'string' && valeur.trim().length === 0;
}

/** Construit la liste ordonnée des clés candidates, de la plus précise à la plus générique. */
function candidats(champ: string, issue: z.ZodIssue, valeur: unknown): ErreurChamp[] {
  const essais: ErreurChamp[] = [];
  if (estVide(valeur)) essais.push({ cle: `validation.${champ}.obligatoire` });
  switch (issue.code) {
    case 'too_small':
      essais.push({ cle: `validation.${champ}.tropCourt`, valeurs: { min: Number(issue.minimum) } });
      break;
    case 'too_big':
      essais.push({ cle: `validation.${champ}.tropLong`, valeurs: { max: Number(issue.maximum) } });
      break;
    case 'invalid_literal':
      essais.push({ cle: `validation.${champ}.obligatoire` });
      break;
    default:
      break;
  }
  essais.push({ cle: `validation.${champ}.invalide` }, { cle: 'validation.generique' });
  return essais;
}

/**
 * Convertit un `ZodError` en erreurs affichables.
 *
 * `valeursSaisies` sert à distinguer « champ vide » (message « obligatoire ») de « valeur
 * refusée » : `password: ''` échoue sur `too_small`, mais le message utile est « obligatoire ».
 */
export function traduireIssues(
  erreur: z.ZodError,
  valeursSaisies: Readonly<Record<string, unknown>> = {},
): ErreursChamps {
  const resultat: Record<string, ErreurChamp> = Object.create(null) as Record<string, ErreurChamp>;
  for (const issue of erreur.issues) {
    const premier = issue.path[0];
    const champ = typeof premier === 'string' ? premier : 'formulaire';
    if (resultat[champ] !== undefined) continue;
    const retenue = candidats(champ, issue, valeursSaisies[champ]).find((candidat) =>
      instanceI18n.exists(candidat.cle),
    );
    resultat[champ] = retenue ?? { cle: 'validation.generique' };
  }
  return resultat;
}

/** Texte traduit d’une erreur de champ, ou `null` si le champ est valide. */
export function texteErreur(erreurs: ErreursChamps, champ: string, t: TFunction): string | null {
  const erreur = erreurs[champ];
  if (erreur === undefined) return null;
  return String(t(erreur.cle, erreur.valeurs ?? {}));
}
